import { Button, TooltipDesktop } from '@cian/ui-kit';
import { IconActionCompareAdd16, IconActionCompareAdded16 } from '@cian/ui-kit-design-tokens/icons';

import { type FC, type SyntheticEvent, useCallback, useMemo } from 'react';

import { useCardContext } from '@/microfrontends/listing/shared/context/CardContext';
import { cardOfferDataSelector } from '@/microfrontends/listing/shared/context/selectors/cardData';

import { useCompareOfferAction } from '../../../hooks/card/useCompareOfferAction';
import { ControlTooltip } from '../components/ControlTooltip';
import { ControlWrapper } from '../components/Controls';
import { FakeControlWrapper } from '../components/FakeControlWrapper';

export const ComparisonContainer: FC = () => {
  const cardContext = useCardContext();
  const { comparisonStatus } = cardOfferDataSelector(cardContext);
  const compareOffer = useCompareOfferAction();

  const handleCompareOffer = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      compareOffer();
    },
    [compareOffer],
  );

  const { description, isAdded, isUnavailable } = useMemo(
    () => ({
      description: (comparisonStatus && comparisonStatus.description) || null,
      isAdded: comparisonStatus ? comparisonStatus.status === 'added' : false,
      isUnavailable: !comparisonStatus || comparisonStatus?.status === 'unavailable',
    }),
    [comparisonStatus],
  );

  if (!comparisonStatus) {
    return null;
  }

  const button = isUnavailable ? (
    <FakeControlWrapper>
      <Button
        disabled
        fullWidth
        beforeIcon={<IconActionCompareAdd16 color="gray60_100" />}
        data-mark="ComparisonControl"
        size="XS"
        theme="fill_primary"
      />
    </FakeControlWrapper>
  ) : (
    <Button
      beforeIcon={
        isAdded ? (
          <IconActionCompareAdded16 color="accent-main-primary" />
        ) : (
          <IconActionCompareAdd16 color="gray60_100" />
        )
      }
      data-mark="ComparisonControl"
      size="XS"
      theme="stroke_secondary"
      onClick={handleCompareOffer}
    />
  );

  return (
    <ControlWrapper visible={true}>
      {description ? (
        <TooltipDesktop placement="left" theme="black" title={<ControlTooltip text={description} />}>
          {button}
        </TooltipDesktop>
      ) : (
        button
      )}
    </ControlWrapper>
  );
};
